'use client';

import React from 'react';
import { QuoteCardComponent } from '../types/content-components.types.js';
import { ComponentIcon } from './ComponentIcon.js';
import { useComponentAnimation } from './hooks/useComponentAnimation.js';

interface QuoteCardProps {
  component: QuoteCardComponent;
}

export const QuoteCard: React.FC<QuoteCardProps> = ({ component }) => {
  const { props } = component;
  const { ref, isVisible } = useComponentAnimation(props.animation);

  const getVariantColors = () => {
    switch (props.variant) {
      case 'primary':
        return { bg: 'bg-learn-sage-50', accent: 'border-learn-sage-400', text: 'text-learn-sage-900', quote: 'text-learn-sage-800' };
      case 'highlight':
        return { bg: 'bg-learn-clay-50', accent: 'border-learn-clay-400', text: 'text-learn-clay-900', quote: 'text-learn-clay-800' };
      case 'info':
        return { bg: 'bg-learn-sky-50', accent: 'border-learn-sky-400', text: 'text-learn-sky-900', quote: 'text-learn-sky-800' };
      default:
        return { bg: 'bg-white', accent: 'border-gray-300', text: 'text-gray-900', quote: 'text-gray-700' };
    }
  };

  const colors = getVariantColors();
  const isLarge = props.size === 'large';

  const animationClass = isVisible
    ? props.animation === 'fade-in'
      ? 'animate-fadeIn'
      : props.animation === 'slide-up'
      ? 'animate-slideUp'
      : ''
    : '';

  return (
    <div
      ref={ref}
      className={`w-full ${colors.bg} border-l-4 ${colors.accent} rounded-r-xl p-6 md:p-8 shadow-sm ${animationClass}`}
      style={{ opacity: isVisible ? undefined : 0 }}
    >
      {/* Icon */}
      {props.icon && (
        <div className="mb-4">
          <ComponentIcon icon={props.icon} className={`w-6 h-6 ${colors.text}`} />
        </div>
      )}

      {/* Quote */}
      <blockquote className={`${isLarge ? 'text-xl md:text-2xl' : 'text-lg md:text-xl'} ${colors.quote} font-medium leading-relaxed italic mb-4`}>
        "{props.quote}"
      </blockquote>

      {/* Author */}
      {props.author && (
        <div className={`text-sm ${colors.text} font-semibold`}>
          — {props.author}
          {props.context && (
            <span className="text-gray-600 font-normal ml-2">
              ({props.context})
            </span>
          )}
        </div>
      )}
    </div>
  );
};

